import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { UserService } from '../../../core/services/core.user.service';
import { GfTranslateService } from '../../../core/services/core.translate.service';
import { marker } from '../../../core/services/ngx-translate-extract-marker';

@Injectable()
export class KanbanStrategyService {

    private map: any = {
        'transition-field-required': marker('Sorry, {{fieldName}} is required for this update'),
        'transition-not-defined': marker('Sorry, this update to {{fieldName}} is not allowed'),
        'transition-role-not-allowed': marker("You don't have permission to update {{fieldName}}"),
        'overwrite': marker('Another user changed this Tracker Item')
    };

    constructor(
        private userService: UserService,
        private translateService: GfTranslateService
    ) {
    }

    private getField(alias: string, tracker: any): any {
        return tracker?.fields?.[alias] || {};
    }

    public getUpdatePath(cfg) {
        return _.get(cfg, 'valuePath', false);
    };

    public getUpdateValue(cfg, ti) {
        var path = this.getUpdatePath(cfg);
        if (path) {
            return _.get(ti, path + '.id', 0);
        }
        return false;
    }

    public isChanged(options, ti, col, row) {
        var colUpdate = this.getUpdateValue(options.columns, ti);

        if (col) {
            if (colUpdate !== false && colUpdate != col.id) return true;
        }

        var rowUpdate = this.getUpdateValue(options.rows, ti);
        if (row) {
            if (rowUpdate !== false && rowUpdate != row.id) return true;
        }

        return false;
    }

    public updateObject(cfg, ti, item) {
        // If item is null, no change can be made
        if (cfg.valuePath && item) {
            var val = this.getUpdateValue(cfg, ti);

            if (val !== false && val != item.id) {
                if (cfg.valuePath == 'priority') {
                    return _.set(_.cloneDeep(ti), cfg.valuePath, item.id);
                } else {
                    return _.set(_.cloneDeep(ti), cfg.valuePath, { id: item.id });
                }
            }
        }
        return ti;
    }

    public getUpdateObject(options, col, row) {
        var colUpdate = this.updateObject(options.columns, {}, col);
        var ret = this.updateObject(options.rows, colUpdate, row);

        return ret;
    }

    public getOptions(params, tracker, acls) {
        var colType = _.get(params, 'c', 'status');
        var rowType = _.get(params, 'r', '');

        return {
            groupBy: rowType,
            columns: this.getGroupingConfig(colType, tracker, params),
            rows: this.getGroupingConfig(rowType, tracker, params),
            canSubmit: _.get(acls, 'canSubmit', false),
            canUpdate: _.get(acls, 'canUpdate', false),
            collapseEmpty: _.get(params, 'collapseEmpty', 0)  // default auto
        };
    }

    public addSorting(options, sortField, sortDir: string = 'asc') {
        return _.extend(
            options,
            {
                sortProperty: sortField,
                sortDirection: sortDir
            }
        );
    }

    public getGroupingConfig(type, tracker, params) {
        if (! type || type == '') return this.getOptionsForUngrouped();

        if (type == 'assignee') {
            return this.getOptionsForAssignees(tracker);
        } else if (type == 'sprint') {
            return this.addSorting(
                this.getOptionsForSprints(tracker), 'sprint'
            );
        } else if (type == 'status') {
            return this.getOptionsForStatus(tracker);
        }

        return this.getOptionsForGenericField(tracker, type, params);
    }

    public getOptionsForUngrouped() {
        return {
            items: [{
                id: 0,
                ungrouped: true
            }],
            titlePath: false,
            valuePath: false
        };
    }

    public getNoneOption() {
        return {
            id: null,
            label: 'None',
            data: null
        };
    }

    public insertNoneOption (items: any, includeNone: boolean = true) {
        if (includeNone) {
            return [this.getNoneOption()].concat(items);
        }

        return items;
    }

    public getOptionsForGenericField(tracker, fieldAlias: string, excludeReleasedReleases = false) {
        let field = _.get(tracker, 'fields.'+fieldAlias, null);
        if (!field) return [];

        let fieldElements = _.get(field, 'fieldElements', []);

        //if this is a release field type and the exclude released releases
        //flag is true, then filter out the released releases
        if (field.fieldType == 8 && excludeReleasedReleases) {
            fieldElements = _.filter(fieldElements, {statusId: 0});
        }

        var items = _.map(
            fieldElements,
            function (e) {
                return {
                    id: e.id,
                    label: e.label,
                    data: e
                };
            }
        );

        var path: string = (fieldAlias == 'priority') ? 'priority' : 'extraFields.'+fieldAlias+'.fieldData';

        return {
            items: this.insertNoneOption(items, !field.isRequired),
            titlePath: 'label',
            valuePath: path
        };
    }

    public getOptionsForStatus(tracker) {
        return {
            items: _.map(
                // Include all statuses
                _.get(tracker, 'fields.status.fieldElements', []),

                // Include only active statuses
                // _.filter(
                //     _.get(tracker, 'fields.status.fieldElements', []),
                //     { statusId: 1 }
                // ),
                function (e) {
                    return {
                        id: e.id,
                        label: e.label,
                        data: e
                    };
                }
            ),
            titlePath: 'label',
            valuePath: 'extraFields.status.fieldData'
        };
    }

    public getOptionsForAssignees(tracker) {
        return {
            items: [{ id: 100, unixName: 'None', 'firstname': 'Nobody', 'lastname': '' }]
                .concat(_.get(tracker, 'rel.assignees', []))
                .map((u) => {
                    return {
                        id: u.id,
                        label: this.userService.fullName(u),
                        data: u
                    };
                }),
            titlePath: 'unixName',
            valuePath: 'rel.assignees[0]'
        };
    }

    public getOptionsForSprints(tracker) {

        var sprints = _.sortBy(
            _.filter(
                _.get(tracker, 'fields.sprint.fieldElements', []),
                function (s) {
                    return s.statusId == 1 || s.statusId == 0;
                }
            ).map((s) => {
                const data = _.cloneDeep(s);
                // Comes from the API with statusId field set,
                // but this is counter-productive for display purposes.
                delete data.statusId;
                return {
                    id: data.id,
                    label: _.has(s, 'label') ? data.label : data.sprintName,
                    data: data
                };
            }),
            'id'
        );

        return {
            items: this.insertNoneOption(sprints),
            titlePath: 'label',
            valuePath: 'sprint.fieldData'
        };
    }

    public getColumnDataForRow(row, options, items) {
        var rowItems = items;

        // Don't partition by row if there is no row grouping.
        if (! row.ungrouped) {
            // Filter items by row
            var rowValuePath = (_.get(options, 'rows.valuePath', '') == 'priority') ?
                _.get(options, 'rows.valuePath', ''):
                _.get(options, 'rows.valuePath', '') + '.id';

            var _filterRow = function(ti) {
                return (row.id == _.get(ti, rowValuePath));
            };

            rowItems = _.filter(items, _filterRow);
        }

        // Partition rowItems by column
        var colValuePath = (_.get(options, 'columns.valuePath', '') == 'priority') ?
            _.get(options, 'columns.valuePath', ''):
            _.get(options, 'columns.valuePath', '') + '.id';

        var _filterCol = function(col, ti) {
            return (col.id == _.get(ti, colValuePath));
        };

        return _.reduce(
            _.get(options, 'columns.items', []),
            (carry, col) => {
                carry[col.id] = _.filter(rowItems, _.partial(_filterCol, col));
                return carry;
            },
            {}
        );
    }

    public getColumnTotalsForRow(carry, row) {
        return _.reduce(
            _.get(row, 'cols', {}),
            (carry, item, key) => {
                carry[key] = _.get(carry, key, 0) + _.size(item);
                return carry;
            },
            carry
        );
    }

    public setColumnTotals(data) {
        data.columnTotals = _.reduce(
            _.get(data, 'rows', []),
            this.getColumnTotalsForRow,
            {}
        );
        return data;
    }

    public collapseGroupBySetting(items, data, collapseEmpty, prefix) {

        /* const collapseOptionMap = {
            (0, 'Auto', 'Use the default behavior for each type of data.'),
            (1, 'Collapse', 'Collapse categories with zero items.'),
            (2, 'Hide', 'Hide categories with zero items.'),
            (3, 'None', 'Leave all categories open, regardless of item count.')
        }; */

        return _.map(
            items,
            (c) => {
                // Collapse the column if its statusId == 0 and the column is empty,
                // if closed has not already been set.
                const fieldId = _.get(c, 'data.id', 'null'),
                        count = (prefix == 'column') ?
                            _.get(data, 'columnTotals.' + fieldId, 0) : 
                            _.get(c, 'total', 0),
                        statusId = _.get(c, 'data.statusId', 1),
                        hasClosed = _.has(c, 'closed');


                switch (collapseEmpty) {
                    // auto = original method
                    case 0:
                        c.hidden = false;
                        if (statusId == 0 && !hasClosed) {
                            c.closed = (count == 0);
                        }
                        break;
                    
                    // collapse = collapse if empty
                    case 1:
                        c.hidden = false;
                        c.closed = (count == 0);
                        break;

                    // hide = hidden if empty
                    case 2:
                        c.hidden = (count == 0);
                        c.closed = false;
                        break;

                    // includes 3: none
                    default:
                        c.closed = false;
                        c.hidden = false;
                        break;
                }
                return c;
            }
        );
    }

    public collapseBySetting(options, data) {

        /* const collapseOptionMap = {
            (0, 'Auto', 'Use the default behavior for each type of data.'),
            (1, 'Collapse', 'Collapse categories with zero items.'),
            (2, 'Hide', 'Hide categories with zero items.'),
            (3, 'None', 'Leave all categories open, regardless of item count.')
        }; */
        const defaultCollapse = {
            columns: 0,
            rows: 0
        };
        const collapseEmpty = _.get(options, 'collapseEmpty', defaultCollapse);  // Default auto

        const updatedColItems = this.collapseGroupBySetting(
            _.get(options, 'columns.items', []),
            data, _.get(collapseEmpty, 'columns', 0), 'column'
        );
        _.set(options, 'columns.items', updatedColItems);

        const updatedRowItems = this.collapseGroupBySetting(
            _.get(options, 'rows.items', []),
            data, _.get(collapseEmpty, 'rows', 0), 'row'
        );
        _.set(options, 'rows.items', updatedRowItems);

        return options;
    }

    public setRowTotals(data) {
        return _.map(
            data,
            (row) => {
                row.total = _.values(row.cols).reduce((carry, item) => {
                    return carry + item.length;
                }, 0);
                return row;
            }
        );
    }

    public getValidationMessages(response: any): {[key: string]: string} {
        const validationList: {[key: string]: string} = {};
        Object.keys(response).forEach((item) => {
          const typesFound = Object.keys(this.map).filter((key) => response[item].some((entry) => entry.type === key));
          if (typesFound.length > 0) {
            validationList[item] = this.map[typesFound[0]];
          }
        });
        return validationList;
      }
    
    public getKanbanMessage(response: any, tracker: any): string {
        const validationList = this.getValidationMessages(response);
        const firstField = Object.keys(validationList)[0];
        if (firstField) {
          const msg = validationList[firstField];
          const field = this.getField(firstField, tracker);
          return this.translateService.translate(msg, field);
        }
        return this.translateService.translate('Sorry, could not update this TrackerItem');
      }
}
